import { ORPCError } from '@orpc/client';
import { z } from 'zod';
import 'zod/compile';

/** What the server reports when completion is blocked by the playbook gate. */
export interface PlaybookGateBlock {
  unmet: Array<{ sopId: string; title: string }>;
}

const UnmetProcedureSchema = z.object({ sopId: z.string(), title: z.string() });
const PlaybookGateEnvelopeSchema = z.object({
  code: z.literal('PLAYBOOK_UNSATISFIED'),
  unmet: z.array(z.unknown()),
});

/**
 * Read a `tasks.complete` failure: PRECONDITION_FAILED carrying
 * `data.code = "PLAYBOOK_UNSATISFIED"` means a required procedure is unmet
 * (including unavailable procedures) and the human may waive it. Anything else is an
 * ordinary error.
 */
export function playbookGateBlockFrom(
  error: unknown,
): PlaybookGateBlock | null {
  if (!(error instanceof ORPCError) || error.code !== 'PRECONDITION_FAILED') {
    return null;
  }
  const parsed = PlaybookGateEnvelopeSchema.safeParse(error.data);
  if (!parsed.success) return null;
  const unmet = parsed.data.unmet.flatMap((entry) => {
    const result = UnmetProcedureSchema.safeParse(entry);
    return result.success ? [result.data] : [];
  });
  return unmet.length > 0 ? { unmet } : null;
}
/** Keep terminal payload normalization shared by normal close and waiver retry. */
export function taskCloseInput(
  id: string,
  outcome: string,
  outcomeReason: string,
) {
  return {
    id,
    ...(outcome.trim() ? { outcome: outcome.trim() } : {}),
    ...(outcomeReason.trim() ? { outcomeReason: outcomeReason.trim() } : {}),
  };
}
